import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Radio, RadioGroup, SelectChangeEvent, Stack, SxProps} from '@mui/material';
import {translations} from '../../../../i18n/translations';
import {RadioLabel} from '../../../shared/forms/radio';
import {DailyCareSelect} from '../../forms/daily-care-select';
import ActionsDrawer, {ActionsDrawerProps} from '../actions-drawer';

const formControlSx: SxProps = {
  maxWidth: '100%',
  '& .MuiFormControlLabel-label': {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.2px',
    color: 'var(--dcm-gray)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

type SortValue = 'name' | 'autonomy' | 'status';
type SortDirection = 'asc' | 'desc';

export interface SortConfiguration {
  value: SortValue;
  direction: SortDirection;
}

export const DEFAULT_SORT_CONFIGURATION: SortConfiguration = {value: 'name', direction: 'asc'};

interface SeniorsSortDrawerProps
  extends Omit<ActionsDrawerProps, 'title' | 'onClearClick' | 'onSaveClick'> {
  sortConfiguration: SortConfiguration;
  onSortConfigurationSave: (sortConfiguration: SortConfiguration) => void;
}

export const SeniorsSortDrawer = ({
  sortConfiguration: currentSortConfiguration,
  onSortConfigurationSave,
  open,
  onDrawerClose,
  ...rest
}: SeniorsSortDrawerProps) => {
  const [sortConfiguration, setSortConfiguration] =
    useState<SortConfiguration>(currentSortConfiguration);

  const {t} = useTranslation();

  const handleValueChange = (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setSortConfiguration({...sortConfiguration, value: value as SortValue});
  };

  const handleDirectionChange = (event: SelectChangeEvent<any>, _child: React.ReactNode) => {
    setSortConfiguration({...sortConfiguration, direction: event.target.value as SortDirection});
  };

  const handleReset = () => {
    setSortConfiguration(DEFAULT_SORT_CONFIGURATION);
  };

  const handleSave = () => {
    onSortConfigurationSave(sortConfiguration);

    // Close the drawer on save
    onDrawerClose();
  };
  return (
    <ActionsDrawer
      title={t(translations.daily_care_log.task_table.drawer.sort.title)}
      onClearClick={handleReset}
      onSaveClick={handleSave}
      onDrawerClose={onDrawerClose}
      open={open}
      {...rest}
    >
      <RadioGroup value={sortConfiguration.value} onChange={handleValueChange}>
        <Stack direction="column" gap={2}>
          <RadioLabel
            value="name"
            label={t(translations.daily_care_log.task_table.drawer.sort.name)}
            control={<Radio />}
            sx={formControlSx}
          />
          <RadioLabel
            value="autonomy"
            label={t(translations.daily_care_log.task_table.drawer.sort.autonomy)}
            control={<Radio />}
            sx={formControlSx}
          />
          <RadioLabel
            value="status"
            label={t(translations.daily_care_log.task_table.drawer.sort.status)}
            control={<Radio />}
            sx={formControlSx}
          />
          <DailyCareSelect
            options={[
              {
                key: 'asc',
                label: t(translations.daily_care_log.task_table.drawer.sort.ascending),
              },
              {
                key: 'desc',
                label: t(translations.daily_care_log.task_table.drawer.sort.descending),
              },
            ]}
            value={sortConfiguration.direction}
            onChange={handleDirectionChange}
          />
        </Stack>
      </RadioGroup>
    </ActionsDrawer>
  );
};
